import { DeviceDTO } from '@eppendorf/vnls-device-service-utils';
import { Badge } from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeviceType } from '$components/app-config/useDeviceType';
import { useDeviceTypeConfig } from '$components/app-config/useDeviceTypeConfig';
import { EditInfoItem } from '$features/devices/device-information/edit-info-item';
import { InfoItem } from '$features/devices/device-information/info-item';
import { InfoList } from '$features/devices/device-information/info-list';
import { UpdateDeviceDialog } from '$features/devices/device-information/update-dialogs/update-device-dialog';
import { useGetDeviceUpdateStatusWithQueryParams } from '$features/devices/devices.api';
import type { DeviceUpdateStatus } from '$features/devices/devices.types';

import styles from '../device-information.module.scss';

export function GeneralInformation({
  data,
  editMode,
}: {
  data: DeviceDTO | undefined;
  editMode: boolean;
}) {
  const { t } = useTranslation();
  const [isUpdateDeviceDialogOpen, setIsUpdateDeviceDialogOpen] = useState(false);

  const deviceTypeConfig = useDeviceTypeConfig(data);
  const rawDeviceType = useDeviceType(data);
  const deviceType = rawDeviceType
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- should work fine
      t(`deviceType.${rawDeviceType}` as any)
    : t('deviceDetail.fallbackHeadlineNameEmpty');
  const { data: updateDeviceStatus } =
    useGetDeviceUpdateStatusWithQueryParams<DeviceUpdateStatus>({
      deviceTypeId: data?.type ?? '',
      softwareVersion: data?.softwareRevision ?? '',
      hardwareRevision: data?.hardwareRevision ?? '',
      enabled: true,
    });

  const isUpdateV2Available = () => !!updateDeviceStatus?.isUpdateAvailable;

  const getSoftwareVersion = () => (
    <>
      {data?.softwareRevision}
      {isUpdateV2Available() && (
        <div
          className={cn(styles.updateBadge, 'm-left-m')}
          onClick={() => {
            setIsUpdateDeviceDialogOpen(!!updateDeviceStatus?.isUpdateAvailable);
          }}
          role="presentation"
        >
          <Badge bordered color="blue" size="small">
            {t('deviceDetail.deviceInformation.updateAvailable')}
          </Badge>
        </div>
      )}
    </>
  );

  return (
    <InfoList>
      <InfoList.Title>
        {t('deviceDetail.deviceInformation.generalInformation')}
      </InfoList.Title>
      <InfoList.Content>
        <InfoItem
          label={t('deviceDetail.deviceInformation.deviceType')}
          value={deviceType}
        />
        {!editMode && (
          <InfoItem
            label={t('deviceDetail.deviceInformation.deviceName')}
            value={data?.name}
          />
        )}
        {editMode && (
          <EditInfoItem
            id="name"
            label={t('deviceDetail.deviceInformation.deviceName')}
          />
        )}
        <InfoItem
          label={t('deviceDetail.deviceInformation.serialNumber')}
          value={data?.serialNumber}
        />
        {!editMode && (
          <InfoItem
            label={t('deviceDetail.deviceInformation.customInventoryId')}
            value={data?.registrationNumber}
          />
        )}
        {editMode && (
          <EditInfoItem
            id="registrationNumber"
            label={t('deviceDetail.deviceInformation.customInventoryId')}
          />
        )}
        <InfoItem
          label={t('deviceDetail.deviceInformation.manufacturer')}
          value={data?.manufacturer}
        />
        <InfoItem
          label={t('deviceDetail.deviceInformation.deviceModel')}
          value={data?.model}
        />
        <InfoItem
          label={t('deviceDetail.deviceInformation.softwareVersion')}
          value={getSoftwareVersion()}
        />
      </InfoList.Content>

      {isUpdateV2Available() && (
        <InfoList.Dialog>
          <UpdateDeviceDialog
            deviceTypeId={data?.type}
            deviceConfig={deviceTypeConfig}
            isOpen={isUpdateDeviceDialogOpen}
            onOpenChange={(status) => setIsUpdateDeviceDialogOpen(status)}
            updateStatus={updateDeviceStatus}
          />
        </InfoList.Dialog>
      )}
    </InfoList>
  );
}
